/**
 * base64url <-> ArrayBuffer conversions and WebAuthn option/credential
 * shape translation between the plaintr-api JSON contract and the native
 * `navigator.credentials.create()/get()` APIs.
 */
import { isRunningInPlaintrWrapper } from "../appUpdate/wrapperEnvironment";

/** Decode a base64url string into an ArrayBuffer. */
export function base64UrlToBuffer(base64Url) {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const buffer = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    buffer[i] = rawData.charCodeAt(i);
  }
  return buffer.buffer;
}

/** Encode an ArrayBuffer/TypedArray into a base64url string. */
export function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** True when the current browser supports the WebAuthn APIs this app needs. */
export function isWebAuthnSupported() {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.credentials?.create === "function" &&
    typeof navigator.credentials?.get === "function"
  );
}

/** A cancelled prompt or no usable credential both surface as `NotAllowedError`. */
export function isPasskeyCancelledError(error) {
  return error?.name === "NotAllowedError";
}

/**
 * Wrapper-only gate: passkey UI is restricted to the Plaintr mobile wrapper
 * WebView for this first rollout, via the shared wrapper detector.
 */
export function isPasskeyUiAvailable() {
  return isRunningInPlaintrWrapper() && isWebAuthnSupported();
}

/** Best-effort WebAuthn Signal API call; returns whether it was accepted. */
export async function signalRevokedCredential({ rpId, credentialId }) {
  if (
    !rpId ||
    !credentialId ||
    typeof window === "undefined" ||
    typeof window.PublicKeyCredential?.signalUnknownCredential !== "function"
  ) {
    return false;
  }

  try {
    await window.PublicKeyCredential.signalUnknownCredential({
      rpId,
      credentialId,
    });
    return true;
  } catch {
    // Not fatal: the fallback guidance covers this case.
    return false;
  }
}

/** Options for `navigator.credentials.create()` from the registration/options response. */
export function transformRegistrationOptions(options) {
  return {
    publicKey: {
      ...options,
      challenge: base64UrlToBuffer(options.challenge),
      user: {
        ...options.user,
        id: base64UrlToBuffer(options.user.id),
      },
      excludeCredentials: (options.excludeCredentials || []).map(
        (credential) => ({
          ...credential,
          id: base64UrlToBuffer(credential.id),
        })
      ),
    },
  };
}

/** Options for `navigator.credentials.get()` from the authentication/options response. */
export function transformAuthenticationOptions(options) {
  return {
    publicKey: {
      ...options,
      challenge: base64UrlToBuffer(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((credential) => ({
        ...credential,
        id: base64UrlToBuffer(credential.id),
      })),
    },
  };
}

/** Serialize a `navigator.credentials.create()` result for registration/verify. */
export function serializeRegistrationCredential(credential) {
  const response = credential.response;
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      transports:
        typeof response.getTransports === "function"
          ? response.getTransports()
          : [],
    },
  };
}

/** Serialize a `navigator.credentials.get()` result for authentication/verify. */
export function serializeAuthenticationCredential(credential) {
  const response = credential.response;
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle
        ? bufferToBase64Url(response.userHandle)
        : null,
    },
  };
}
